import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BsStars, BsLightningCharge, BsBook, BsAward,
  BsGeoAlt, BsGlobe, BsGlobeAmericas, BsBuilding,
} from 'react-icons/bs';
import { trackEvent } from '../../utils/analytics';

const curriculumCards = [
  { icon: BsBook, label: 'CBSE', descriptor: 'Grades 1–10', color: 'from-blue-500 to-indigo-600', to: '/mode?category=school', tip: 'Explore CBSE Questions' },
  { icon: BsAward, label: 'ICSE', descriptor: 'Grades 1–10', color: 'from-emerald-500 to-teal-600', to: '/mode?category=school', tip: 'Explore ICSE Questions' },
  { icon: BsGeoAlt, label: 'Kerala State', descriptor: 'SCERT Syllabus', color: 'from-orange-500 to-amber-600', to: '/mode?category=school', tip: 'Explore Kerala State Questions' },
  { icon: BsGlobe, label: 'IGCSE', descriptor: 'Cambridge Curriculum', color: 'from-purple-500 to-violet-600', to: '/mode?category=school', tip: 'Explore IGCSE Questions' },
  { icon: BsGlobeAmericas, label: 'IB', descriptor: 'International Baccalaureate', color: 'from-pink-500 to-rose-600', to: '/mode?category=school', tip: 'Explore IB Questions' },
  { icon: BsBuilding, label: 'Engineering', descriptor: 'Semester-wise Practice', color: 'from-cyan-500 to-blue-600', to: '/mode?category=college', tip: 'Start Engineering Practice' },
];

const floatingPositions = [
  { x: '6%', y: '18%', delay: 0 },
  { x: '84%', y: '14%', delay: 0.4 },
  { x: '4%', y: '62%', delay: 0.8 },
  { x: '87%', y: '58%', delay: 1.2 },
  { x: '12%', y: '82%', delay: 0.6 },
  { x: '82%', y: '80%', delay: 1.0 },
];

export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-x-clip pt-20 pb-12 lg:pb-0">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-600/20 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 left-1/3 w-[600px] h-[600px] bg-accent-600/15 rounded-full blur-[128px]" />
        <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-brand-400/10 rounded-full blur-[100px]" />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating Cards - Desktop only */}
      <div className="hidden lg:block absolute inset-0 z-20 pointer-events-none">
        {curriculumCards.map((card, i) => {
          const animClass = floatingPositions[i].delay > 0.6 ? 'animate-float-delayed' : 'animate-float';
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: floatingPositions[i].delay + 0.8, duration: 0.6, type: 'spring' }}
              className={`absolute ${animClass} pointer-events-auto`}
              style={{ left: floatingPositions[i].x, top: floatingPositions[i].y }}
            >
              <Link to={card.to} title={card.tip} onClick={() => trackEvent("curriculum_card_clicked", { board: card.label, source: "landing_hero" })}>
                <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3 hover:scale-110 hover:shadow-lg hover:shadow-brand-500/20 transition-all duration-300 cursor-pointer group">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center group-hover:shadow-md transition-shadow`}>
                    <card.icon className="text-white text-lg" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-200 leading-tight">{card.label}</span>
                    <span className="text-[11px] text-gray-400 leading-tight">{card.descriptor}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6 lg:mb-8"
          >
            <BsStars className="text-accent-400 text-sm" />
            <span className="text-xs sm:text-sm font-medium text-gray-300">Powered by Advanced AI</span>
          </motion.div>

          {/* Headline - smaller on mobile */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black font-display tracking-tight leading-[0.95] mb-4 lg:mb-6">
            <span className="text-white">Master Concepts.</span>
            <br />
            <span className="gradient-text-hero">Ace Exams.</span>
          </h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-400 max-w-2xl mx-auto mb-6 lg:mb-10 leading-relaxed px-1"
          >
            Practice past papers, get AI-powered explanations, and build exam confidence one question at a time.
          </motion.p>

          {/* Mobile/Tablet Curriculum Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="lg:hidden mb-8 -mx-5 sm:-mx-6 lg:-mx-8"
          >
            <div className="flex gap-3 overflow-x-auto pb-3 px-5 sm:px-6 lg:px-8 snap-x snap-mandatory scrollbar-hide"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {curriculumCards.map((card, i) => (
                <Link
                  key={i}
                  to={card.to}
                  title={card.tip}
                  onClick={() => trackEvent("curriculum_card_clicked", { board: card.label, source: "landing_hero_mobile" })}
                  className="snap-center flex-shrink-0"
                >
                  <div className="glass rounded-xl px-4 py-3 flex items-center gap-2.5 hover:bg-white/[0.08] transition-all duration-300 cursor-pointer group min-w-[150px] sm:min-w-[160px]">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${card.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <card.icon className="text-white text-sm" />
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="text-sm font-semibold text-gray-200 leading-tight">{card.label}</span>
                      <span className="text-[10px] text-gray-400 leading-tight whitespace-nowrap">{card.descriptor}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="flex flex-col items-center gap-4 lg:gap-5 mb-10 lg:mb-16"
          >
            <Link to="/signup" onClick={() => trackEvent("signup_started", { source: "landing_hero_cta" })} className="btn-primary text-base sm:text-lg !px-10 sm:!px-12 !py-3.5 lg:!py-4 group">
              <span className="relative z-10 flex items-center gap-2">
                Start Practicing
                <BsLightningCharge className="group-hover:animate-pulse" />
              </span>
            </Link>

            {/* Trust Benefits */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-xs sm:text-sm text-gray-400"
            >
              {[
                'Free for everyone',
                '5 AI assists without login',
                'Unlimited AI assists after login',
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{benefit}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dark-500 to-transparent" />

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}
